// src/hooks/useAudioMeter.ts
//
// Hook autonome de mesure audio par source.
//
// Principe :
//   - Un AudioContext + AnalyserNode est créé PAR SOURCE, à la demande.
//   - La mesure est déclenchée quand une source est "observée" (modale ouverte
//     ou source active dans le deck).
//   - Quand la source n'est plus observée, le contexte est fermé et les
//     ressources libérées.
//
// Sources supportées :
//   • 'device'  → getUserMedia({ audio: true }) sur le deviceId associé
//   • 'remote'  → piste audio du MediaStream de la connexion distante
//   • 'url'     → piste audio du currentStream si c'est la source active
//   • 'jingle'  → element audio du jingle en cours (MediaElementAudioSourceNode)
//   • 'screen'  → piste audio du stream de partage d'écran

import { useRef, useCallback, useEffect } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AudioLevel {
  left:  number; // 0–100
  right: number; // 0–100
}

export type ObservedSourceType = 'device' | 'remote' | 'url' | 'jingle' | 'screen';

export interface ObserveOptions {
  /** deviceId micro pour source 'device' */
  audioDeviceId?: string;
  /** MediaStream déjà acquis (remote, url active, screen) */
  stream?: MediaStream;
  /** Élément <video> du jingle en cours de lecture */
  jingleEl?: HTMLVideoElement | null;
}

interface MeterEntry {
  ctx:       AudioContext;
  analyserL: AnalyserNode;
  analyserR: AnalyserNode;
  dataL:     Uint8Array;
  dataR:     Uint8Array;
  rafId:     number;
  level:     AudioLevel;
  stream?:   MediaStream;
  ownStream: boolean; // true = on a ouvert ce stream, on doit le fermer
}

export interface UseAudioMeterReturn {
  /**
   * Démarre la mesure pour une source.
   * Idempotent : si la source est déjà observée, ne fait rien.
   */
  observe: (
    sourceId:   string,
    sourceType: ObservedSourceType,
    options:    ObserveOptions,
  ) => Promise<void>;

  /**
   * Arrête la mesure et libère les ressources pour une source.
   */
  unobserve: (sourceId: string) => void;

  /**
   * Retourne le niveau courant (0–100) pour une source.
   * Retourne { left: 0, right: 0 } si la source n'est pas observée.
   */
  getLevel: (sourceId: string) => AudioLevel;

  /**
   * Enregistre un callback appelé à chaque frame de mesure pour une source.
   * Remplace le callback précédent pour ce sourceId.
   */
  subscribe: (sourceId: string, cb: (level: AudioLevel) => void) => void;

  /**
   * Supprime le callback pour une source.
   */
  unsubscribe: (sourceId: string) => void;

  /**
   * Libère toutes les ressources (appelé automatiquement au démontage).
   */
  dispose: () => void;
}

// ── Constantes ────────────────────────────────────────────────────────────────

const FFT_SIZE     = 256;
const SMOOTHING    = 0.15; // réactif — faible lissage
const MIN_DECIBELS = -70;
const MAX_DECIBELS = -10;

// ── Helpers ───────────────────────────────────────────────────────────────────

function peakLevel(data: Uint8Array): number {
  let peak = 0;
  for (let i = 0; i < data.length; i++) {
    if (data[i] > peak) peak = data[i];
  }
  return (peak / 255) * 100;
}

function makeAnalyser(ctx: AudioContext): AnalyserNode {
  const a = ctx.createAnalyser();
  a.fftSize               = FFT_SIZE;
  a.smoothingTimeConstant = SMOOTHING;
  a.minDecibels           = MIN_DECIBELS;
  a.maxDecibels           = MAX_DECIBELS;
  return a;
}

function connectStream(
  ctx:       AudioContext,
  stream:    MediaStream,
  analyserL: AnalyserNode,
  analyserR: AnalyserNode,
): void {
  const audioTracks = stream.getAudioTracks();
  if (!audioTracks.length) return;
  const src = ctx.createMediaStreamSource(stream);
  try {
    const split = ctx.createChannelSplitter(2);
    src.connect(split);
    split.connect(analyserL, 0);
    split.connect(analyserR, 1);
  } catch {
    // Mono : même signal sur L et R
    src.connect(analyserL);
    src.connect(analyserR);
  }
}

function connectMediaElement(
  ctx:       AudioContext,
  el:        HTMLVideoElement,
  analyserL: AnalyserNode,
  analyserR: AnalyserNode,
): void {
  try {
    const src = ctx.createMediaElementSource(el);
    try {
      const split = ctx.createChannelSplitter(2);
      src.connect(split);
      split.connect(analyserL, 0);
      split.connect(analyserR, 1);
    } catch {
      src.connect(analyserL);
      src.connect(analyserR);
    }
    // Reconnecter à destination pour que le son continue de sortir
    src.connect(ctx.destination);
  } catch (e) {
    console.warn('[useAudioMeter] createMediaElementSource échoué:', e);
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAudioMeter(): UseAudioMeterReturn {
  const entries   = useRef<Map<string, MeterEntry>>(new Map());
  const callbacks = useRef<Map<string, (level: AudioLevel) => void>>(new Map());

  const cleanupEntry = useCallback((entry: MeterEntry) => {
    cancelAnimationFrame(entry.rafId);
    try { entry.ctx.close(); } catch {}
    if (entry.ownStream && entry.stream) {
      entry.stream.getTracks().forEach(t => t.stop());
    }
  }, []);

  const startMeasureLoop = useCallback((sourceId: string, entry: MeterEntry) => {
    const loop = () => {
      if (!entries.current.has(sourceId)) return;
      entry.analyserL.getByteFrequencyData(entry.dataL);
      entry.analyserR.getByteFrequencyData(entry.dataR);
      entry.level = {
        left:  peakLevel(entry.dataL),
        right: peakLevel(entry.dataR),
      };
      const cb = callbacks.current.get(sourceId);
      if (cb) cb(entry.level);
      entry.rafId = requestAnimationFrame(loop);
    };
    entry.rafId = requestAnimationFrame(loop);
  }, []);

  const observe = useCallback(async (
    sourceId:   string,
    sourceType: ObservedSourceType,
    options:    ObserveOptions,
  ): Promise<void> => {
    if (entries.current.has(sourceId)) return;

    const ctx       = new AudioContext();
    const analyserL = makeAnalyser(ctx);
    const analyserR = makeAnalyser(ctx);
    const dataL     = new Uint8Array(analyserL.frequencyBinCount);
    const dataR     = new Uint8Array(analyserR.frequencyBinCount);

    const entry: MeterEntry = {
      ctx, analyserL, analyserR, dataL, dataR,
      rafId: 0,
      level: { left: 0, right: 0 },
      ownStream: false,
    };

    entries.current.set(sourceId, entry);

    if (ctx.state === 'suspended') {
      await ctx.resume().catch(() => {});
    }

    if (sourceType === 'device') {
      try {
        const constraints: MediaStreamConstraints = {
          audio: options.audioDeviceId
            ? { deviceId: { exact: options.audioDeviceId }, echoCancellation: false, noiseSuppression: false, autoGainControl: false }
            : { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          video: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        entry.stream    = stream;
        entry.ownStream = true;
        connectStream(ctx, stream, analyserL, analyserR);
      } catch (e) {
        console.warn(`[useAudioMeter] getUserMedia échoué pour ${sourceId}:`, e);
      }

    } else if (
      (sourceType === 'remote' || sourceType === 'url' || sourceType === 'screen')
      && options.stream
    ) {
      entry.stream    = options.stream;
      entry.ownStream = false;
      connectStream(ctx, options.stream, analyserL, analyserR);

    } else if (sourceType === 'jingle' && options.jingleEl) {
      connectMediaElement(ctx, options.jingleEl, analyserL, analyserR);

    } else {
      entries.current.delete(sourceId);
      cleanupEntry(entry);
      return;
    }

    startMeasureLoop(sourceId, entry);
  }, [cleanupEntry, startMeasureLoop]);

  const unobserve = useCallback((sourceId: string) => {
    const entry = entries.current.get(sourceId);
    if (!entry) return;
    entries.current.delete(sourceId);
    cleanupEntry(entry);
  }, [cleanupEntry]);

  const getLevel = useCallback((sourceId: string): AudioLevel => {
    return entries.current.get(sourceId)?.level ?? { left: 0, right: 0 };
  }, []);

  const subscribe = useCallback((sourceId: string, cb: (level: AudioLevel) => void) => {
    callbacks.current.set(sourceId, cb);
  }, []);

  const unsubscribe = useCallback((sourceId: string) => {
    callbacks.current.delete(sourceId);
  }, []);

  const dispose = useCallback(() => {
    entries.current.forEach(entry => cleanupEntry(entry));
    entries.current.clear();
    callbacks.current.clear();
  }, [cleanupEntry]);

  useEffect(() => {
    return () => dispose();
  }, [dispose]);

  return { observe, unobserve, getLevel, subscribe, unsubscribe, dispose };
}
